import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import logowhite from "../../assets/logo-white.png";
import { indexStore } from "../../store/indexStore";
import i18n from "../../i18n";
import BranchModel from "../../Models/BranchModel";
import Iterator from "../utils/Iterator";
import Visibility from "../utils/Visibility";
import { socialMediaTypes } from "../../enum/socialMediaType";
import { SettingsSocialMedia } from "../../Models/SettingsModel";

function FooterPages() {
  const { t } = useTranslation();
  const { lang, changeLang, branches, name, socialMedia } = indexStore();

  useEffect(() => {
    const handleLanguageChange = () => {
      i18n.changeLanguage(lang);
      document.documentElement.dir = i18n.dir();
    };

    if (lang) {
      handleLanguageChange();
    } else {
      changeLang(localStorage.getItem("Lang") || "ar");
    }
  }, [lang, changeLang]);

  const toggleLanguage = () => {
    changeLang(lang === "ar" ? "en" : "ar");
  };

  return (
    <footer className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-onPrimary">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 lg:gap-12">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-4 lg:col-span-2"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2 mb-4"
            >
              <img
                src={logowhite}
                alt="Kreish Restaurant Logo"
                className="h-20 md:h-24 w-auto object-contain"
              />
            </motion.div>
            <p className="text-sm md:text-base leading-relaxed text-onPrimary/90 max-w-md">
              {t("HomePage.aboutDescription")}
            </p>
            <p className="text-xs md:text-sm mt-6 text-onPrimary/70">
              {t("gloabl.AllRightsReserved")} &copy; {new Date().getFullYear()} {t("HomePage.heroTitle")}
            </p>
          </motion.div>

          {/* Quick Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-onPrimary">
              {t("footer.qickLinks")}
            </h3>
            <ul className="space-y-3 md:space-y-4">
              <motion.li whileHover={{ x: lang === "ar" ? -5 : 5 }}>
                <a
                  href="/privacy-policy"
                  rel="noreferrer"
                  className="text-sm md:text-base hover:underline text-onPrimary/90 hover:text-onPrimary transition-colors block"
                >
                  {t("pages.privacyPolicy")}
                </a>
              </motion.li>
              <motion.li whileHover={{ x: lang === "ar" ? -5 : 5 }}>
                <a
                  href="/terms-of-service"
                  rel="noreferrer"
                  className="text-sm md:text-base hover:underline text-onPrimary/90 hover:text-onPrimary transition-colors block"
                >
                  {t("pages.termsOfService")}
                </a>
              </motion.li>
              <motion.li whileHover={{ x: lang === "ar" ? -5 : 5 }}>
                <a
                  href="/delete-account"
                  rel="noreferrer"
                  className="text-sm md:text-base hover:underline text-onPrimary/90 hover:text-onPrimary transition-colors block"
                >
                  {t("pages.deleteAccountGuide")}
                </a>
              </motion.li>
              <motion.li whileHover={{ x: lang === "ar" ? -5 : 5 }}>
                <a
                  href="/contact"
                  className="text-sm md:text-base hover:underline text-onPrimary/90 hover:text-onPrimary transition-colors block"
                >
                  {t("pages.contact")}
                </a>
              </motion.li>
            </ul>
          </motion.div>

          {/* Language & Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-onPrimary">
              {t("footer.contactUs")}
            </h3>

            {/* Language Toggle */}


            {/* Contact Info */}
            <ul className="space-y-3 md:space-y-4">
              {/* Branches Addresses */}
              <Iterator
                data={branches}
                render={(branch: BranchModel) => {
                  const addressKey = name === "name_en" ? "address_en" : "address_ar";
                  const address = branch[addressKey as keyof BranchModel] as string;
                  const branchName = branch[name === "name_en" ? "name_en" : "name_ar" as keyof BranchModel] as string;

                  return (
                    <Visibility key={branch.id} visible={!!address || !!branch.phone}>
                      <div className="space-y-3">
                        {/* Branch Name and Address */}
                        {address && (
                          <motion.li
                            whileHover={{ x: lang === "ar" ? -5 : 5 }}
                            className="group"
                          >
                            <a
                              href={branch.address_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex hover:underline items-center gap-4 p-3 rounded-lg hover:bg-onPrimary/10 transition-colors group"
                            >
                              <div className="p-2 bg-onPrimary/10 group-hover:bg-onPrimary/20 rounded-lg transition-colors flex-shrink-0">
                                <MapPin className="text-onPrimary text-xl md:text-2xl" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-onPrimary/70 mb-1">
                                  {branchName}
                                </p>
                                <span className="text-sm md:text-base text-onPrimary/90 group-hover:text-onPrimary transition-colors flex-1">
                                  {address}
                                </span>
                              </div>
                            </a>
                          </motion.li>
                        )}
                        
                        {/* Phone Number */}
                        {branch.phone && (
                          <motion.li
                            whileHover={{ x: lang === "ar" ? -5 : 5 }}
                            className="group"
                          >
                            <a
                              className="flex items-center gap-4 p-3 rounded-lg hover:bg-onPrimary/10 transition-colors"
                              href={`tel:${branch.phone}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <div className="p-2 bg-onPrimary/10 group-hover:bg-onPrimary/20 rounded-lg transition-colors">
                                <Phone className="text-onPrimary text-xl md:text-2xl" />
                              </div>
                              <div className="flex-1">
                                {branchName && (
                                  <p className="text-xs text-onPrimary/70 mb-1">
                                    {branchName}
                                  </p>
                                )}
                                <span dir="ltr" className="text-sm md:text-base text-onPrimary/90 group-hover:text-onPrimary transition-colors">
                                  {branch.phone}
                                </span>
                              </div>
                            </a>
                          </motion.li>
                        )}
                      </div>
                    </Visibility>
                  );
                }}
              />

              <motion.li
                whileHover={{ x: lang === "ar" ? -5 : 5 }}
                className="group"
              >
                <a
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-onPrimary/10 transition-colors"
                  href="mailto:info@krieshrestaurant.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="p-2 bg-onPrimary/10 group-hover:bg-onPrimary/20 rounded-lg transition-colors">
                    <Mail className="text-onPrimary text-xl md:text-2xl" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm md:text-base text-onPrimary/90 group-hover:text-onPrimary transition-colors">
                      info@krieshrestaurant.com
                    </span>
                  </div>
                </a>
              </motion.li>
            </ul>
            <div className="mt-6 flex">
              <motion.button
                onClick={toggleLanguage}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className=" md:w-auto px-6 py-3 rounded-xl bg-onPrimary/10 hover:bg-onPrimary/20 border-2 border-onPrimary/20 hover:border-onPrimary/40 text-onPrimary font-semibold text-sm md:text-base transition-all duration-300 flex items-center justify-center gap-2"
              >
                <span>{lang === "ar" ? "العربية" : "English"}</span>
              </motion.button>
            </div>
          </motion.div>

        </div>

        {/* Social Icons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 md:mt-12 pt-8 md:pt-10 border-t border-onPrimary/20 flex flex-wrap gap-4 md:gap-6 justify-center"
        >
          <Iterator
            data={socialMedia || []}
            render={(social: SettingsSocialMedia) => {
              let url = social.link;
              if (!url.startsWith("https://") && !url.startsWith("http://") && !url.startsWith("mailto:") && !url.startsWith("tel:")) {
                url = "https://" + url;
              }

              const icon = socialMediaTypes[social.type];

              return (
                <motion.a
                  key={social.id}
                  whileHover={{ scale: 1.2, y: -3 }}
                  whileTap={{ scale: 0.9 }}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="p-3 rounded-full bg-onPrimary/10 hover:bg-onPrimary/20 transition-all duration-300"
                  aria-label={social.title}
                  dir={url.startsWith("tel:") || url.startsWith("mailto:") ? "ltr" : undefined}
                >
                  <div className="text-onPrimary [&>svg]:w-5 [&>svg]:h-5">
                    {icon}
                  </div>
                </motion.a>
              );
            }}
          />
          <motion.a
            whileHover={{ scale: 1.2, y: -3 }}
            whileTap={{ scale: 0.9 }}
            href="mailto:info@krieshrestaurant.com"
            dir="ltr"
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-full bg-onPrimary/10 hover:bg-onPrimary/20 transition-all duration-300"
            aria-label="Email"
          >
            <Mail size={20} className="text-onPrimary" />
          </motion.a>

        </motion.div>
      </div>
    </footer>
  );
}

export default FooterPages;
